import { ConfirmDialogComponent } from "@/components/confirm-dialog/confirm-dialog.component";
import { DataLoadingComponent } from "@/components/data-loading/data-loading.component";
import {
  PhotoExtend,
  PhotosViewerComponent,
} from "@/components/photos-viewer/photos-viewer.component";
import { TooltipDirective } from "@/directives/tooltip/tooltip.directive";
import { ObjectUpdatedType } from "@/enums/ObjectUpdatedType.enum";
import { AppConstants } from "@/helpers/app.constants";
import { PropertyHelper } from "@/helpers/property.helper";
import { UserHelper } from "@/helpers/user.helper";
import { AlertService } from "@/services/alert.service";
import { AppConfigService } from "@/services/app-config.service";
import { NgFor, NgIf } from "@angular/common";
import {
  ApplicationRef,
  Component,
  createComponent,
  CUSTOM_ELEMENTS_SCHEMA,
  Injector,
  Input,
  OnDestroy,
  OnInit,
} from "@angular/core";
import { MatButtonModule } from "@angular/material/button";
import { MatCheckboxModule } from "@angular/material/checkbox";
import { MatDialog } from "@angular/material/dialog";
import { MatExpansionModule } from "@angular/material/expansion";
import { MatIconModule } from "@angular/material/icon";
import {
  Photo,
  PhotoCategory,
  PhotosService,
  RealProperty,
  RescueService,
} from "@eemo/api-client";
import { concatMap, from, reduce, Subscription } from "rxjs";
import { PropertyPhotoCardComponent } from "../../../components/property-photo-card/property-photo-card.component";

class PhotoCategoryExtend {
  category: PhotoCategory;
  name: string;
  panelDescription: string;
  expanded: boolean;
  photos: Photo[];
}

@Component({
  selector: "app-property-photos",
  templateUrl: "./property-photos.component.html",
  styleUrls: ["./property-photos.component.scss"],
  standalone: true,
  imports: [
    NgIf,
    MatExpansionModule,
    NgFor,
    PropertyPhotoCardComponent,
    MatCheckboxModule,
    MatButtonModule,
    MatIconModule,
    TooltipDirective,
    DataLoadingComponent,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class PropertyPhotosComponent implements OnInit, OnDestroy {
  @Input() prop: RealProperty;
  @Input() rescueMode = false;
  @Input() slideShowOnly = false;

  thumbnailSize = "640x360";
  mainPhoto: Photo;
  allPhotos: PhotoExtend[] = [];
  categories: PhotoCategory[];
  photoCategories: PhotoCategoryExtend[] = [];
  canUpdateProperty = false;

  subs: Subscription[] = [];

  multiselectEnabled = false;
  selectedPhotos: string[] = [];
  deletingPhotos = false;

  constructor(
    private photoService: PhotosService,
    private rescueService: RescueService,
    private appConfigService: AppConfigService,
    private propHelper: PropertyHelper,
    private userHelper: UserHelper,
    private dialog: MatDialog,
    private alertService: AlertService,
    private injector: Injector,
    private appRef: ApplicationRef
  ) {}

  ngOnInit() {
    if (!this.prop) {
      this.prop = this.appConfigService.selectedProp;
      this.rescueMode = this.userHelper.isRescueService();
    }
    if (this.rescueMode) {
      this.categories = [PhotoCategory.OUTSIDE];
    } else {
      this.categories = this.propHelper
        .getPhotoCategories()
        .map((c) => PhotoCategory[c]);
    }
    this.loadPhotoList();

    this.subs.push(
      this.appConfigService.objectUpdated.subscribe((type) => {
        if (type === ObjectUpdatedType.PHOTO) {
          this.refreshPhotoList();
        }
      })
    );
    this.canUpdateProperty = this.userHelper.canUpdateProperty(this.prop);
  }

  ngOnDestroy(): void {
    this.subs.forEach((s) => s.unsubscribe());
  }

  refreshPhotoList() {
    this.multiselectEnabled = false;
    this.selectedPhotos = [];
    this.loadPhotoList();
  }

  loadPhotoList() {
    this.photoCategories = [];
    this.allPhotos = [];
    this.categories.forEach((categ) => {
      this.photoCategories.push({
        category: categ,
        name: this.propHelper.getPhotoCategoryName(categ),
        panelDescription: "",
        expanded: false,
        photos: [],
      });
    });
    if (this.rescueMode) {
      this.subs.push(
        this.rescueService
          .getRealPropertyRescuePhotos(this.prop.uid)
          .subscribe((photos) => this.loadPhotos(photos))
      );
    } else {
      this.subs.push(
        this.photoService
          .getRealPropertyPhotos(this.prop.uid)
          .subscribe((photos) => this.loadPhotos(photos))
      );
    }
  }

  get hasPhoto() {
    return this.allPhotos.length > 0;
  }

  loadPhotos(photos: Photo[]) {
    photos.forEach((photo) => {
      this.allPhotos.push({
        photo,
        categoryName: this.propHelper.getPhotoCategoryName(photo.category),
        url: this.propHelper.getPhotoImageUrl(photo),
      });
      const photoCateg = this.photoCategories.find(
        (p) => p.category === photo.category
      );
      photoCateg.photos.push(photo);
      photoCateg.expanded = true;
      if (photo.main) {
        this.mainPhoto = photo;
      }
    });
    this.photoCategories.forEach(
      (p) => (p.panelDescription = this.getPanelDescription(p.photos))
    );
  }

  showImage(photo: Photo) {
    window.scroll(0, 0);
    const compRef = createComponent(PhotosViewerComponent, {
      environmentInjector: this.appRef.injector,
      elementInjector: this.injector,
    });

    compRef.instance.photos = this.allPhotos;
    compRef.instance.onClose = () => {
      compRef.destroy();
      compRef.location.nativeElement.remove();
    };
    compRef.instance.onComponentUpdate = () => {
      compRef.changeDetectorRef.detectChanges();
    };
    compRef.changeDetectorRef.detectChanges();
    // need to be done after detect changes to trigger the slide to function
    compRef.instance.index = this.findPhotoIndex(photo);

    document
      .getElementById(AppConstants.SWIPER_OVERLAY_DIV_ID)
      ?.appendChild(compRef.location.nativeElement);
  }

  findPhotoIndex(photo: Photo) {
    const pe = this.allPhotos.find((pe) => pe.photo === photo);
    return this.allPhotos.indexOf(pe);
  }

  mainPhotoIsUpdated() {
    this.appConfigService.triggerMainPhotoUpdated();
  }

  photoUpdated() {
    this.appConfigService.triggerObjectUpdated(ObjectUpdatedType.PHOTO);
  }

  getPanelDescription(photos: Photo[]) {
    if (!photos) {
      return "Aucune photo";
    }
    switch (photos.length) {
      case 0:
        return "Aucune photo";
      case 1:
        return "1 photo";
      default:
        return photos.length + " photos";
    }
  }

  toggleMultiSelect() {
    this.multiselectEnabled = !this.multiselectEnabled;
    this.selectedPhotos = [];
  }

  toggleSelectPhoto(photo: Photo) {
    const photoId = photo.uid;
    if (this.selectedPhotos.includes(photoId)) {
      this.selectedPhotos = this.selectedPhotos.filter((p) => p !== photoId);
    } else {
      this.selectedPhotos.push(photoId);
    }
  }

  deleteSelectedPhotos() {
    // prevents to open multiple dialog
    if (this.dialog.openDialogs.length === 0) {
      const photoStr = this.selectedPhotos.length > 1 ? "photos" : "photo";
      const dialogRef = this.dialog.open(ConfirmDialogComponent, {
        // width: '25rem',
        data: {
          message: `Confirmez-vous la suppression de ${this.selectedPhotos.length} ${photoStr} ?`,
        },
      });
      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          this.doDeleteSelectedPhotos();
        }
      });
    }
  }

  doDeleteSelectedPhotos() {
    this.deletingPhotos = true;
    from(this.selectedPhotos)
      .pipe(
        concatMap((photoId) => this.photoService.deletePhoto(photoId)), // execute requests sequentially
        reduce((acc, _) => acc + 1, 0) // count the number of deleted photos
      )
      .subscribe({
        next: (count) => {
          const photoStr =
            count > 1
              ? `${count} photos ont été supprimées`
              : "1 photo a été supprimée";
          this.appConfigService.triggerObjectUpdated(ObjectUpdatedType.PHOTO);
          this.alertService.success(photoStr);
          this.deletingPhotos = false;
        },
        error: () => (this.deletingPhotos = false),
      });
  }
}
